import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faSearch, faCaretUp, faCaretDown } from '@fortawesome/pro-regular-svg-icons';
import { getConciergeRequestCodeRedemptions } from '../../api/noir';
import ShopmyImage from '../General/ShopmyImage';
import './CodeRedemptionsModal.scss';

// Table of who redeemed a code and how many times, opened from the per-code Redemptions
// cell. The full list loads once; search and sort run client-side. perUserLimit (when set)
// is the "/ limit" denominator.
const CodeRedemptionsModal = ({ code, onClose }) => {
  const [redemptions, setRedemptions] = useState([]);
  const [perUserLimit, setPerUserLimit] = useState(null);
  const [totalUses, setTotalUses] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    getConciergeRequestCodeRedemptions(code)
      .then(({ redemptions: rows, perUserLimit: limit, totalUses: uses }) => {
        setRedemptions(rows || []);
        setPerUserLimit(limit ?? null);
        setTotalUses(uses || 0);
      })
      .catch(err => window.ALERT.error(err || 'Failed to load redemptions.'))
      .finally(() => setLoading(false));
  }, [code]);

  const redemptionCount = useMemo(() => redemptions.reduce((sum, r) => sum + Number(r.count), 0), [redemptions]);
  const untracked = Math.max(0, totalUses - redemptionCount);

  const visibleRedemptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? redemptions.filter(r => (r.name || '').toLowerCase().includes(term) || (r.username || '').toLowerCase().includes(term) || String(r.User_id).includes(term))
      : redemptions;
    return [...filtered].sort((a, b) => (sortDir === 'desc' ? b.count - a.count : a.count - b.count));
  }, [redemptions, search, sortDir]);

  return (
    <div className='code-redemptions-modal react-confirm-alert-body'>
      <div className='redemptions-header'>
        <h1>
          Redemptions for <span className='code-name'>{code}</span>
        </h1>
        <div className='search-input-wrapper'>
          <FontAwesomeIcon icon={faSearch} className='search-icon' />
          <input type='text' className='search-input' placeholder='Search by name, username, or ID...' value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div className='redemptions-placeholder'>
          <FontAwesomeIcon icon={faSpinner} spin />
          Loading redemptions...
        </div>
      ) : !redemptions.length ? (
        <div className='redemptions-placeholder'>{untracked > 0 ? "This code's older redemptions aren’t linked to specific users." : 'No redemptions yet.'}</div>
      ) : (
        <>
          <div className='redemptions-count'>
            {redemptionCount.toLocaleString()} redemption{redemptionCount !== 1 ? 's' : ''} by {redemptions.length.toLocaleString()} user{redemptions.length !== 1 ? 's' : ''}
            {untracked > 0 && <span className='redemptions-untracked-note'> · some older redemptions aren’t linked to specific users</span>}
          </div>
          <div className='redemptions-table'>
            <div className='redemptions-grid header'>
              <div className='th'>User</div>
              <div className='th redemptions-col sortable' onClick={() => setSortDir(d => (d === 'desc' ? 'asc' : 'desc'))} title='Sort by redemption count'>
                Redemptions <FontAwesomeIcon icon={sortDir === 'desc' ? faCaretDown : faCaretUp} />
              </div>
            </div>
            {!visibleRedemptions.length ? (
              <div className='redemptions-empty'>No users match your search.</div>
            ) : (
              visibleRedemptions.map(r => {
                const profileUrl = r.username ? `https://shopmy.us/${r.username}` : null;
                const userContent = (
                  <>
                    {r.image ? <ShopmyImage className='user-avatar' source={r.image} alt={r.name || ''} /> : <div className='user-avatar placeholder' />}
                    <div className='user-meta'>
                      <span className='user-name'>{r.name || `User #${r.User_id}`}</span>
                      <span className='user-sub'>
                        {r.username ? `@${r.username} · ` : ''}ID: {r.User_id}
                      </span>
                    </div>
                  </>
                );
                return (
                  <div key={r.User_id} className='redemptions-grid row'>
                    <div className='td user-cell'>
                      {profileUrl ? (
                        <a className='user-link' href={profileUrl} target='_blank' rel='noopener noreferrer'>
                          {userContent}
                        </a>
                      ) : (
                        <div className='user-link'>{userContent}</div>
                      )}
                    </div>
                    <div className='td redemptions-col'>
                      <span className='redeemed'>{Number(r.count).toLocaleString()}</span>
                      {perUserLimit != null && <span className='limit'> / {Number(perUserLimit).toLocaleString()}</span>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      <div className='react-confirm-alert-button-group'>
        <button className='cancel-btn' onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

CodeRedemptionsModal.propTypes = {
  code: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

export default CodeRedemptionsModal;
